import { DocumentTextIcon } from '@heroicons/react/24/outline'
import { ChevronRightIcon } from '@heroicons/react/24/solid'
import Image from 'next/image'
import { FC } from 'react'

import { useRequestTypes } from '@/api/models/requests/hooks/useRequestTypes'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { cn } from '@/app/utils'
import { useFilteredRequestTypes } from '@/components/entities/passes/hooks/useFilteredRequestTypes'
import { AVAILABLE_PASS_TYPES } from '@/components/entities/passes/model/constants'
import { ErrorCard } from '@/components/pages/plugs'
import { Badge } from '@/components/shared/badge'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import { useRequestNavigation } from '@/components/widgets/request/hooks/useRequestNavigation'

const AvailablePasses: FC = () => {
  const { reachGoal } = useYandexMetrika()

  const { data: requestTypes, error: typesError } = useRequestTypes()
  const { navigateToCreateFormPass } = useRequestNavigation()
  const availableRequestTypes = useFilteredRequestTypes(AVAILABLE_PASS_TYPES)

  const handleRedirectToCreatePass = (passType: RequestTypeAlias) => {
    navigateToCreateFormPass(passType)

    if (passType === RequestTypeAlias.RentPassAuto) {
      reachGoal(YandexMetrikaEvent.PASSES_RENT_CREATE)
    }
  }

  if (typesError) {
    return (
      <div className="mt-[200px]">
        <ErrorCard />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-y-16 xs:gap-y-8">
      {availableRequestTypes.map(type => (
        <button
          key={type.alias}
          className={cn(
            'flex w-full items-center justify-between gap-x-10 rounded-16 px-16 py-28 lg:p-16',
            type.isNew ? 'bg-bronze-5' : 'bg-grey-10'
          )}
          onClick={() => handleRedirectToCreatePass(type.alias)}
        >
          <div className="flex gap-12 xs:flex-col">
            <div className="relative flex h-40 w-40 flex-shrink-0 items-center justify-center rounded-12 p-5">
              {type.iconLink ? (
                <Image
                  className="object-contain"
                  width={30}
                  height={30}
                  src={type.iconLink}
                  alt={type.name}
                />
              ) : (
                <DocumentTextIcon className="h-full w-full text-white" />
              )}
            </div>
            <div className="flex flex-col justify-center">
              <div className="flex gap-8">
                <p className="text-left font-bold text-deepBlack text-20 xs:text-18">
                  {type.name}
                </p>
                {type.isNew && (
                  <Badge
                    variant="light-bronze"
                    size="x-small"
                    className="font-normal !text-16"
                  >
                    NEW
                  </Badge>
                )}
              </div>
              {type.description && (
                <p className="text-left font-pro text-grey-70 text-16 xs:text-14">
                  {type.description}
                </p>
              )}
            </div>
          </div>
          <div className="flex h-full flex-shrink-0 items-center">
            <ChevronRightIcon className="block h-24 w-24" />
          </div>
        </button>
      ))}
      {!requestTypes && <>Нет доступных услуг</>}
    </div>
  )
}

export { AvailablePasses }
